import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Timeline } from "@/components/ui/timeline";
import { FeaturesSectionWithHoverEffects } from "@/components/ui/feature-section-with-hover-effects";
import { HeroParallax } from "@/components/ui/hero-parallax";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import TimelineImageGallery from "@/components/TimelineImageGallery";
import { useStaticTranslation } from "@/lib/staticTranslations";

import { 
  Heart, 
  Shield, 
  Users, 
  Award, 
  Gem, 
  Clock,
  MapPin,
  Phone,
  Mail,
  Star,
  Sparkles
} from "lucide-react";

// Hero image - using existing asset
import heroImage from "@/assets/hero-jewelry.jpg";

// Timeline gallery images (existing assets)
import ringImage from "@/assets/ring-1.jpg";
import braceletImage from "@/assets/bracelet-1.jpg";
import earringsImage from "@/assets/earrings-1.jpg";
import necklaceImage from "@/assets/necklace-1.jpg";
import heroNecklaceImage from "@/assets/hero-necklace-star.jpg";
import ethicalRingHandImage from "@/assets/ethical-ring-hand.jpg";

const AboutUs = () => {
  const { t, isHebrew } = useStaticTranslation("about");
  const { t: seoT } = useStaticTranslation("seo");
  const dir = isHebrew ? "rtl" : "ltr";

  // Timeline data - translated
  const timelineEvents = [
    {
      year: 1984,
      title: t("founding", "ייסוד החנות"),
      description: t("foundingDesc", "דוד ואלברט בורמד מייסדים את אליס תכשיטים בבאר שבע"),
      images: [
        { src: heroNecklaceImage, alt: "תכשיט קלאסי - תחילת הדרך" },
        { src: ringImage, alt: "טבעת זהב - קולקציה ראשונה" },
        { src: braceletImage, alt: "צמיד זהב - עבודת יד" },
        { src: earringsImage, alt: "עגילים קלאסיים - סגנון מסורתי" },
      ],
    },
    {
      year: 1990,
      title: t("expansion", "התרחבות המגוון"),
      description: t("expansionDesc", "הרחבת קולקציית התכשיטים והשעונים היוקרתיים"),
      images: [
        { src: earringsImage, alt: "עגילי יהלום - הרחבת הקולקציה" },
        { src: necklaceImage, alt: "שרשרת יוקרתית - עיצוב חדש" },
        { src: braceletImage, alt: "צמיד מעוצב - מגוון רחב" },
        { src: ringImage, alt: "טבעת יהלום - דגמים חדשים" },
      ],
    },
    {
      year: 2000,
      title: t("leadingSouth", "מובילים בדרום"),
      description: t("leadingSouthDesc", "הפיכה לחנות התכשיטים המובילה באזור הדרום"),
      images: [
        { src: ringImage, alt: "טבעת יהלום - סטנדרט איכות" },
        { src: heroImage, alt: "תצוגת תכשיטים - מובילים בדרום" },
        { src: necklaceImage, alt: "שרשרת מעוצבת - קולקציה מובילה" },
        { src: earringsImage, alt: "עגילים יוקרתיים - קהל נאמן" },
      ],
    },
    {
      year: 2010,
      title: t("internationalPartners", "שיתופי פעולה בינלאומיים"),
      description: t("internationalDesc", "התחלת שיתופי פעולה עם יצרנים מובילים מאירופה"),
      images: [
        { src: ethicalRingHandImage, alt: "טבעת על יד - שיתופי פעולה" },
        { src: earringsImage, alt: "עגילים יוקרתיים - מותגים בינלאומיים" },
        { src: heroNecklaceImage, alt: "תכשיט יוקרתי - השראה עולמית" },
        { src: necklaceImage, alt: "שרשרת יוקרתית - עיצוב אירופאי" },
      ],
    },
    {
      year: 2020,
      title: t("digitalRevolution", "מהפכה דיגיטלית"),
      description: t("digitalDesc", "השקת האתר החדש ומעבר למכירות אונליין"),
      images: [
        { src: heroImage, alt: "אתר אליס תכשיטים - חוויית אונליין" },
        { src: ringImage, alt: "מוצר אונליין - טבעת" },
        { src: braceletImage, alt: "מוצר אונליין - צמיד" },
        { src: necklaceImage, alt: "מוצר אונליין - שרשרת" },
      ],
    },
    {
      year: 2024,
      title: t("fortyYears", "40 שנות מצוינות"),
      description: t("fortyDesc", "חוגגים ארבעה עשורים של שירות ואיכות ללא פשרות"),
      images: [
        { src: heroNecklaceImage, alt: "תכשיט חגיגי - 40 שנה" },
        { src: necklaceImage, alt: "שרשרת חגיגית - קולקציית יום הולדת" },
        { src: ringImage, alt: "טבעת חגיגית - 40 שנות מצוינות" },
        { src: braceletImage, alt: "צמיד חגיגי - 40 שנות מצוינות" },
      ],
    },
  ];

  // Statistics data - translated
  const statsData = [
    { value: 1984, label: t("yearFounded", "שנת ייסוד"), suffix: "" },
    { value: 40, label: t("yearsExperience", "שנות ניסיון"), suffix: "+" },
    { value: 100, label: t("satisfaction", "שביעות רצון"), suffix: "%" },
    { value: 10000, label: t("happyCustomers", "לקוחות מרוצים"), suffix: "+" },
  ];

  const heroRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);
  
  const { ref: storyRef, isVisible: storyVisible } = useScrollAnimation();
  const { ref: valuesRef, isVisible: valuesVisible } = useScrollAnimation();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();
  const { ref: contactRef, isVisible: contactVisible } = useScrollAnimation();
  
  // Badge visibility based on footer
  const [showBadge, setShowBadge] = useState(true);
  
  useEffect(() => {
    const handleScroll = () => {
      if (footerRef.current) {
        const footerRect = footerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        setShowBadge(footerRect.top > windowHeight - 100);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Animated counters
  const [countersStarted, setCountersStarted] = useState(false);
  const [counterValues, setCounterValues] = useState(statsData.map(() => 0));
  
  useEffect(() => {
    if (statsVisible && !countersStarted) {
      setCountersStarted(true);
      statsData.forEach((stat, index) => {
        const duration = 2000;
        const steps = 60;
        const increment = stat.value / steps;
        let current = 0;
        
        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.value) {
            current = stat.value;
            clearInterval(timer);
          }
          setCounterValues(prev => {
            const newValues = [...prev];
            newValues[index] = Math.round(current);
            return newValues;
          });
        }, duration / steps);
      });
    }
  }, [statsVisible, countersStarted]);

  return (
    <PageTransition>
      <SEO
        title={seoT("aboutTitle", "אודות אליס תכשיטים | הסיפור שלנו")}
        description={seoT("aboutDescription", "כבר 40 שנה אליס תכשיטים מספקת שירות מעולה ומוצרים איכותיים. הכירו את הסיפור שלנו, הערכים והמחויבות שלנו לאיכות.")}
        canonical="https://alice.jewelry/about"
      />
      <div className="min-h-screen" dir={dir}>
        <Header />
        
        <main>
          {/* Hero Section */}
          <section ref={heroRef} className="relative h-[40vh] sm:h-[45vh] md:h-[50vh] min-h-[300px] sm:min-h-[350px] md:min-h-[400px] overflow-hidden">
            <motion.div 
              className="absolute inset-0"
              style={{ y: heroY, opacity: heroOpacity }}
            >
              <img 
                src={heroImage} 
                alt="אליס תכשיטים" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
            </motion.div>
            
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="text-white/90 text-[10px] sm:text-xs md:text-sm tracking-[0.15em] sm:tracking-[0.2em] uppercase mb-2 sm:mb-3 block">
                  {t("since", "מאז 1984")}
                </span>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-3 sm:mb-4">
                  {t("ourStory", "הסיפור שלנו")}
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-white/80 max-w-xl mx-auto leading-relaxed px-2">
                  {t("tradition", "ארבעה עשורים של מסורת, איכות ושירות מעולה")}
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="absolute bottom-4 sm:bottom-6"
              >
                <div className="w-4 sm:w-5 h-6 sm:h-8 border-2 border-white/50 rounded-full flex justify-center">
                  <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-0.5 sm:w-1 h-2 sm:h-2.5 bg-white/70 rounded-full mt-1 sm:mt-1.5"
                  />
                </div>
              </motion.div>
            </div>
          </section>

          <Separator className="bg-border/50 h-[2px]" />

          {/* Story Section */}
          <section ref={storyRef} className="py-8 sm:py-10 md:py-12 lg:py-16 bg-muted/30 relative overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 md:px-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={storyVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-3xl mx-auto text-center"
              >
                <span className="text-primary text-xs sm:text-sm tracking-widest uppercase mb-2 sm:mb-3 block">
                  {t("ourStory", "הסיפור שלנו")}
                </span>
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-foreground mb-4 sm:mb-6">
                  {t("tradition", "מסורת של מצוינות")}
                </h2>
                
                <div className={`text-xs sm:text-sm md:text-base text-muted-foreground space-y-3 sm:space-y-4 leading-relaxed ${isHebrew ? 'text-right' : 'text-left'}`}>
                  <p className={`${isHebrew ? 'first-letter:text-3xl sm:first-letter:text-4xl first-letter:font-serif first-letter:font-bold first-letter:text-primary first-letter:float-right first-letter:ml-1.5 sm:first-letter:ml-2 first-letter:mt-0.5 sm:first-letter:mt-1' : 'first-letter:text-3xl sm:first-letter:text-4xl first-letter:font-serif first-letter:font-bold first-letter:text-primary first-letter:float-left first-letter:mr-1.5 sm:first-letter:mr-2 first-letter:mt-0.5 sm:first-letter:mt-1'}`}>
                    {t("storyP1", "בשנת 1984, דוד ואלברט בורמד פתחו את דלתות אליס תכשיטים בבאר שבע, עם חזון פשוט אך שאפתני - להביא תכשיטים איכותיים ושירות מעולה לכל לקוח.")}
                  </p>
                  <p>
                    {t("storyP2", "מאז, הפכנו לשם דבר בעולם התכשיטים בדרום הארץ. כל תכשיט שאנחנו מציעים נבחר בקפידה, כל לקוח מקבל יחס אישי, וכל רגע אצלנו הוא חוויה.")}
                  </p>
                  <p>
                    {t("storyP3", "אנחנו מאמינים שתכשיט הוא יותר מאשר אביזר - הוא סיפור, זיכרון, רגע של אושר שנשמר לנצח.")}
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          <Separator className="bg-border/50 h-[2px]" />

          {/* Timeline Section */}
          <section className="bg-background overflow-hidden">
            <Timeline
              data={timelineEvents.map((event) => ({
                title: String(event.year),
                content: (
                  <div className={isHebrew ? "text-right" : "text-left"}>
                    <p className="text-foreground text-sm md:text-base font-semibold mb-2">
                      {event.title}
                    </p>
                    <p className="text-muted-foreground text-sm md:text-base font-normal mb-8 leading-relaxed">
                      {event.description}
                    </p>
                    <TimelineImageGallery images={event.images} />
                  </div>
                ),
              }))}
            />
          </section>

          <Separator className="bg-border/50 h-[2px]" />

          {/* Values Section */}
          <section ref={valuesRef} className="py-8 sm:py-12 md:py-16 lg:py-20 bg-muted/30">
            <div className="container mx-auto px-4 sm:px-6 md:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={valuesVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="text-center mb-6 sm:mb-8 md:mb-10"
              >
                <span className="text-primary text-[10px] sm:text-xs tracking-widest uppercase mb-2 sm:mb-3 block">
                  {t("ourValues", "הערכים שלנו")}
                </span>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-foreground mb-2 sm:mb-3">
                  {t("whatGuidesUs", "מה מנחה אותנו")}
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground max-w-xl mx-auto px-2">
                  {t("valuesSubtitle", "הערכים האלה הם הבסיס לכל מה שאנחנו עושים")}
                </p>
              </motion.div>

              <FeaturesSectionWithHoverEffects />
            </div>
          </section>

          <Separator className="bg-border/50 h-[2px]" />

          {/* Statistics Section */}
          <section ref={statsRef} className="py-6 sm:py-8 md:py-10 lg:py-12 bg-gradient-to-br from-primary/5 via-background to-gold/5 overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 md:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={statsVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="text-center mb-5 sm:mb-6 md:mb-8"
              >
                <span className="text-primary text-[10px] sm:text-xs tracking-widest uppercase mb-2 sm:mb-3 block">
                  {t("inNumbers", "במספרים")}
                </span>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-foreground">
                  {t("ourSuccess", "ההצלחה שלנו")}
                </h2>
              </motion.div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
                {statsData.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={statsVisible ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-1 sm:mb-2">
                      {stat.suffix === "%" ? `${counterValues[index]}%` : 
                       stat.suffix === "+" ? `${counterValues[index].toLocaleString()}+` : 
                       counterValues[index].toLocaleString()}
                    </div>
                    <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
              <div className="h-4 sm:h-6 md:h-10" />
            </div>
          </section>

          <Separator className="bg-border/50 h-[2px]" />

          {/* Contact/Store Section */}
          <section ref={contactRef} className="py-6 sm:py-8 md:py-10 lg:py-14 bg-muted/30">
            <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
              <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
                <motion.div
                  initial={{ opacity: 0, x: isHebrew ? -30 : 30 }}
                  animate={contactVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8 }}
                  className="px-1 sm:px-2 md:px-4"
                >
                  <span className="text-primary text-[10px] sm:text-xs tracking-widest uppercase mb-2 sm:mb-3 block">
                    {t("visitUs", "בואו לבקר")}
                  </span>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-foreground mb-4 sm:mb-6">
                    {t("visitUs", "החנות שלנו")}
                  </h2>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-0.5">{t("addressLabel", "כתובת")}</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">הרצל 58, באר שבע</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-0.5">{t("phoneLabel", "טלפון")}</h3>
                        <a href="tel:079-5575576" className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors">
                          079-5575576
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-0.5">{t("emailLabel", "אימייל")}</h3>
                        <a href="mailto:info@alicejewelry.net" className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors break-all">
                          info@alicejewelry.net
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-0.5">{t("hoursLabel", "שעות פעילות")}</h3>
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">ראשון, שני, רביעי, חמישי: 10:00-19:00</p>
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">שלישי, שישי וערבי חג: 09:45-14:00</p>
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">שבתות וחגים: סגור</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: isHebrew ? 30 : -30 }}
                  animate={contactVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative"
                >
                  <div className="aspect-[4/3] overflow-hidden elegant-border bg-muted rounded-none">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3388.3!2d34.6475!3d31.8044!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDQ4JzE2LjAiTiAzNMKwMzgnNTEuMCJF!5e0!3m2!1siw!2sil!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0, minHeight: '200px' }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="מיקום החנות"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          <Separator className="bg-border/50 h-[2px]" />

          {/* CTA Section */}
          <section className="py-8 sm:py-10 md:py-12 lg:py-14 bg-gradient-to-br from-primary via-primary to-gold/80 text-primary-foreground">
            <div className="container mx-auto px-4 sm:px-6 md:px-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold mb-3 sm:mb-4">
                  מוכנים להתחיל?
                </h2>
                <p className="text-sm sm:text-base opacity-90 max-w-lg mx-auto mb-4 sm:mb-6 px-2">
                  בואו לגלות את הקולקציה שלנו ולמצוא את התכשיט המושלם עבורכם
                </p>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center px-4">
                  <Button asChild size="default" variant="secondary" className="px-4 sm:px-6 text-sm">
                    <Link to="/collections/all">{t("viewCollections", "לכל הקטגוריות")}</Link>
                  </Button>
                  <Button asChild size="default" className="px-4 sm:px-6 text-sm bg-white text-primary hover:bg-white/90 border-none">
                    <Link to="/contact">{t("contactUs", "צור קשר")}</Link>
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Fixed Badge - Ticket style with pulse */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ 
              opacity: showBadge ? 1 : 0, 
              x: showBadge ? 0 : 20,
              y: showBadge ? 0 : 50 
            }}
            transition={{ duration: 0.3 }}
            className={`fixed ${isHebrew ? 'right-0' : 'left-0'} bottom-6 sm:bottom-8 z-40`}
            style={{
              pointerEvents: showBadge ? 'auto' : 'none',
            }}
          >
            <div className="relative">
              <motion.div
                animate={{ 
                  scale: [1, 1.15, 1],
                  opacity: [0.5, 0, 0.5]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className={`absolute inset-0 bg-primary ${isHebrew ? 'rounded-s-lg' : 'rounded-e-lg'}`}
              />
              
              <div className={`relative bg-gradient-to-br from-primary via-primary to-gold/80 text-primary-foreground px-2 sm:px-3 py-1.5 sm:py-2 shadow-xl ${isHebrew ? 'rounded-s-lg border-r-4' : 'rounded-e-lg border-l-4'} border-gold`}>
                <div className="text-center">
                  <span className="text-base sm:text-lg font-bold leading-none block">40+</span>
                  <span className="text-[8px] sm:text-[9px] font-medium opacity-90 block mt-0.5">
                    {t("yearsOfExperience", "שנות ניסיון")}
                  </span>
                </div>
                
                <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent ${isHebrew ? 'rounded-s-lg' : 'rounded-e-lg'}`} />
              </div>
            </div>
          </motion.div>
        </main>

        <div ref={footerRef}>
          <Footer2 />
        </div>
      </div>
    </PageTransition>
  );
};

export default AboutUs;
